import Box from '@mui/material/Box';
import { Announcement } from '@internal/backstage-plugin-announcement-common';
import { useAnnouncementBanner } from '../../hooks/useAnnouncementBanner';
import { AnnouncementBannerItem } from './AnnouncementBannerItem';

interface AnnouncementBannerProps {
  announcements?: Announcement[];
  /**
   * When true the banner unmounts itself as soon as the user authenticates,
   * handing off to the in-app banner rendered inside Root.tsx.
   * Used by the AppRootElementBlueprint registration in App.tsx so that
   * unauthenticated users see public announcements without showing a duplicate
   * banner once they sign in.
   */
  hideWhenAuthenticated?: boolean;
}

/**
 * Displays active announcements as an in-flow banner at the top of the page.
 *
 * When placed via AppRootElementBlueprint (root-level, outside the sign-in
 * gate), pass `hideWhenAuthenticated` so the banner disappears once the user
 * signs in and the in-app instance inside Root.tsx takes over.
 *
 * If `announcements` is provided the component renders those directly.
 * Otherwise it fetches from the backend via useAnnouncementBanner.
 */
export const AnnouncementBanner = ({
  announcements: initialAnnouncements,
  hideWhenAuthenticated = false,
}: AnnouncementBannerProps) => {
  const { visibleAnnouncements, handleClose, isAuthenticated } =
    useAnnouncementBanner(initialAnnouncements);

  if (hideWhenAuthenticated && isAuthenticated) {
    return null;
  }

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      {visibleAnnouncements.map(announcement => (
        <AnnouncementBannerItem
          key={announcement.id}
          announcement={announcement}
          onDismiss={handleClose}
        />
      ))}
    </Box>
  );
};
